import { useCallback, useEffect, useMemo, useState } from 'react';
import _, { isNil } from 'lodash';
import { Trans } from '@lingui/macro';
import { useFormContext } from 'react-hook-form';

import DataTable from '../data-table/DataTable';
import { useArrayLoader, useQueryPostLoader } from '../data-table/Loaders';
import NgpTwoButtonDialog from '../dialog-two-button/NgpTwoButtonDialog';
import { ROWSIZE } from '../data-table/DataTableConstants';
import { SELECTIONMODE_MULTI } from '../data-table/SelectionContext';
import { useDataService } from '../../services/ApiDataService';
import { filterEntities } from './ListSelectorUtils';

export default function ListSelectorDialog({
    gridColumns,
    availableDataSource,
    availableLoaderFunction,
    availableSelectAll = true,
    idsDataSource,
    addButtonText,
    showDialog,
    setShowDialog,
    selected,
    updateSelection,
    resolveInvertedFunction,
    agentList,
    setTheAgents,
    sorters,
    dataArray = null,
    dataArraySearchFields = [],
    dialogMaxWidth,
    dialogFullWidth,
}) {
    const [availableDataItems, setAvailableDataItems] = useState([]);
    const [confirmDisabled, setConfirmDisabled] = useState(false);
    const [tableState, setTableState] = useState(undefined);
    const [tableSelection, setTableSelection] = useState(undefined);
    const availableLoaderFn = !isNil(dataArray) ? useArrayLoader : availableLoaderFunction || useQueryPostLoader;
    const staticDataFilterCallback = useCallback(
        (entities, filters) => filterEntities(entities, filters, dataArraySearchFields),
        [dataArraySearchFields]
    );
    const availableLoader = !isNil(dataArray)
        ? availableLoaderFn(availableDataItems, staticDataFilterCallback)
        : availableLoaderFn(availableDataSource, selected, true);
    const { setValue, getValues } = useFormContext();
    const dataService = useDataService();
    const columns = useMemo(() => setNotResizable(gridColumns), [gridColumns]);
    const staticAvailableData = useMemo(() => {
        if (dataArray) {
            return dataArray.filter((dataItem) => !selected.includes(dataItem._id));
        }
    }, [dataArray, selected]);
    const ID_FIELD = '_id';

    useEffect(() => {
        if (dataArray) {
            setAvailableDataItems(staticAvailableData);
        }
    }, [staticAvailableData, dataArray]);

    const handleConfirm = () => {
        if (setTheAgents && tableSelection) {
            // get previously chosen agents so it can be appended to rather than overwritten
            const { agents } = getValues();
            tableSelection.selection.push(...agents);
            if (tableSelection.inverted) {
                const invertedAgents = agentList.filter((agent) => !tableSelection.selection.includes(agent._id));
                tableSelection.selection = invertedAgents.map((agent) => agent._id);
            }
            setTheAgents(tableSelection.selection);
            setValue('agents', tableSelection.selection);
            updateSelection([...selected, ...tableSelection.selection]);
            setShowDialog(false);
            return;
        }

        // Handler for case where we're using a static array of data and an array loader
        if (dataArray && availableDataItems.length) {
            if (tableSelection?.inverted) {
                let tableSelectionInverted = availableDataItems.filter(
                    (availableDataItem) => !tableSelection?.selection?.includes(availableDataItem._id)
                );

                // If a search exists, apply the search to the inverted data set
                if (tableState?.search) {
                    const searchFilter = { value: tableState.search };
                    tableSelectionInverted = filterEntities(
                        tableSelectionInverted,
                        [searchFilter],
                        dataArraySearchFields
                    );
                }

                updateSelection([...selected, ...tableSelectionInverted.map((dataItem) => dataItem._id)]);
            } else {
                updateSelection([...selected, ...tableSelection.selection]);
            }

            setShowDialog(false);
            return;
        }

        if (!idsDataSource) {
            if (_.isFunction(resolveInvertedFunction) && tableSelection.inverted) {
                resolveInvertedFunction(tableSelection.selection).then((ids) => {
                    const combinedAgents = [...selected, ...ids];
                    updateSelection(combinedAgents);
                });
            } else {
                const combinedAgents = [...selected, ...tableSelection.selection];
                updateSelection(combinedAgents);
            }
            setShowDialog(false);
            return;
        }

        const params = {
            ...idsDataSource.params,
            search: tableState?.search || '',
        };

        return dataService.post(idsDataSource.url, tableSelection, params).then((response) => {
            const combinedAgents = [...selected, ...response];
            updateSelection([...new Set(combinedAgents)]);
            setShowDialog(false);
        });
    };

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setConfirmDisabled(
            !currentTableSelection || (_.isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    function setNotResizable(cols) {
        return cols.map((c) => {
            return { ...c, resizable: false };
        });
    }

    return (
        <NgpTwoButtonDialog
            title={addButtonText}
            confirmButtonText={<Trans>Add</Trans>}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={showDialog}
            setOpen={setShowDialog}
            confirmButtonDisabled={confirmDisabled}
            callerWillCloseOnConfirm
            onConfirm={handleConfirm}
            maxWidth={dialogMaxWidth}
            fullWidth={dialogFullWidth}
        >
            <DataTable
                loader={availableLoader}
                columns={columns}
                disableCSVExport
                disableColumnRemover
                showSelectionColumn
                rowSize={ROWSIZE.SMALL}
                sorters={sorters || availableDataSource?.initialSort}
                visibleRows={10}
                shrink={false}
                onStateChanged={setTableState}
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    allowSelectAll: availableSelectAll,
                    idMapping: ID_FIELD,
                    onChange: handleTableSelectionChange,
                }}
            />
        </NgpTwoButtonDialog>
    );
}
